import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';
import { object, string, shape, bool, func, number } from 'prop-types';

import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import OrgJobInfo from './OrgJobInfo/OrgJobInfo';
import ViewSwitcher from './ViewSwitcher/ViewSwitcher';
import YearOverYearHeader from './YearOverYearHeader/YearOverYearHeader';
import OverallComposites from './OverallComposites/OverallComposites';
import SurveyJobMatches from './SurveyJobMatches/SurveyJobMatches';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import {
  overallComposite as overallCompositePropType,
  surveyJobMatches as surveyJobMatchesPropType,
  surveyJobMatch as surveyJobMatchPropType
} from './propTypes';
import * as yoyActionCreator from './YearOverYearActions';
import { calcYoYChange } from './PayElementTables/PayElementAtTargetTable/helpers';

import './YearOverYearContainer.scss';
import { withRouter } from 'react-router';

export class YearOverYearContainer extends React.PureComponent {
  static propTypes = {
    overallComposite: overallCompositePropType,
    surveyJobMatches: surveyJobMatchesPropType,
    displayIn: string,
    drilldownJob: surveyJobMatchPropType,
    monetaryRounding: number,
    percentRounding: number,
    actions: object,
    selectedPayElement: string,
    hideJobCodeKey: bool,
    jobCodeKeyText: string,
    selectedOrgJob: shape({
      code: string,
      codeKey: string,
      id: string,
      title: string
    }),
    match: shape({
      params: shape({
        orgJobCode: string,
        orgJobCodeKey: string,
        benchmarkYear: string
      })
    }),
    isCompositeLoading: bool,
    isSurveyJobMatchesLoading: bool,
    hideSlideout: func,
    selectedJobId: number,
    selectedBenchmarkYear: number,
    showAsPercentage: bool,
    slideout: shape({
      open: bool,
      slideoutProps: shape({
        orgJobId: number
      }),
      slideoutType: object
    }),
    showOverallComposites: bool,
    launchDarklyFeatureFlags: object,
    errorLoadingSurveyJobMatches: bool,
    showSurveyJobDrilldown: bool,
    defaultBenchmarkYear: number
  };

  constructor(props) {
    super(props);
    this.state = { isPdf: false };
    this._hideSlideout = this._hideSlideout.bind(this);
  }

  componentDidMount() {
    const { actions, selectedJobId, displayIn, defaultBenchmarkYear, match } = this.props;
    if (match.params?.orgJobId) {
      const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });
      //Get Query Strings from Url
      const displayIn = queryString.displayIn ? queryString.displayIn : null;
      const benchmarkYear = queryString.benchmarkYear ? queryString.benchmarkYear : null;
      this.setState({ isPdf: true });
      actions.setYearOverYearDisplayIn(displayIn);
      actions.setYearOverYearBenchmarkYear(benchmarkYear);
      actions.getHeaderInfo(match.params.orgJobId);
    }
    if (match.params?.jobId) {
      actions.setYearOverYearDisplayIn(displayIn);
      actions.setYearOverYearBenchmarkYear(defaultBenchmarkYear);
      actions.getHeaderInfo(match.params.jobId);
    } else {
      if (!selectedJobId) {
        return;
      }
      actions.getHeaderInfo(selectedJobId);
    }
  }

  componentDidUpdate(prevProps) {
    const { actions, selectedOrgJob, selectedJobId, selectedBenchmarkYear, slideout, displayIn, match } = this.props;
    const { isPdf } = this.state;
    const hasSelectedJob = Object.keys(selectedOrgJob).length > 1;
    if (!match?.params?.orgJobId && !match?.params?.jobId && slideout.open && !hasSelectedJob) {
      actions.getHeaderInfo(selectedJobId);
    }
    if ((isPdf || slideout.open) && hasSelectedJob && selectedOrgJob.id !== prevProps.selectedOrgJob.id) {
      actions.getOverallComposite(selectedOrgJob.code, selectedOrgJob.codeKey, selectedBenchmarkYear, displayIn);
    }
  }

  UNSAFE_componentWillUnmount() {
    const { actions } = this.props;
    actions.resetOverallComposite();
  }

  /* Returns an object of the format
    {
      categories: [string] // The benchmark years (or nothing if all null)
      data: [{
        name: string,
        y: number
      }],
      seriesName: string // e.g., Base Salary at 50th
    }
  */
  getChartData(payElements, selectedPayElement, selectedPayElementLabel = 'Base Pay at 50th') {
    const { showAsPercentage, percentRounding } = this.props;

    const payElement = payElements[selectedPayElement];
    const { prevPrevYear, prevYear, currentYear } = payElement;
    const categories = [];
    const data = [];

    categories.push(`${prevPrevYear.label}`);
    if (!showAsPercentage) {
      data.push({
        name: `${prevPrevYear.label}`,
        y: prevPrevYear.value === 'Not Purchased' ? null : prevPrevYear.value
      });
    } else {
      // For showing as percentages, the first year will always be 0
      data.push({
        name: `${prevPrevYear.label}`,
        y: 0
      });
    }

    categories.push(`${prevYear.label}`);
    if (!showAsPercentage) {
      data.push({
        name: `${prevYear.label}`,
        y: prevYear.value === 'Not Purchased' ? null : prevYear.value
      });
    } else {
      data.push({
        name: `${prevYear.label}`,
        y:
          prevPrevYear.value &&
          prevPrevYear.value !== 'Not Purchased' &&
          prevYear.value &&
          prevYear.value !== 'Not Purchased'
            ? Number.parseFloat(calcYoYChange(prevPrevYear.value, prevYear.value, percentRounding))
            : 0
      });
    }

    categories.push(`${currentYear.label}`);
    if (!showAsPercentage) {
      data.push({
        name: `${currentYear.label}`,
        y: currentYear.value
      });
    } else {
      data.push({
        name: `${currentYear.label}`,
        y:
          prevYear.value &&
          prevYear.value !== 'Not Purchased' &&
          currentYear.value &&
          currentYear.value !== 'Not Purchased'
            ? Number.parseFloat(calcYoYChange(prevYear.value, currentYear.value, percentRounding))
            : 0
      });
    }

    return {
      categories,
      data,
      seriesName: selectedPayElementLabel
    };
  }

  getPayElementLabels() {
    const { overallComposite } = this.props;

    return {
      baseSalary: `Base Salary at ${overallComposite.baseSalary.targetElement}`,
      totalCash: `Total Cash at ${overallComposite.totalCash.targetElement}`,
      totalTargetCash: `Total Target Cash at ${overallComposite.totalTargetCash.targetElement}`,
      totalDirect: `Total Direct at ${overallComposite.totalDirect.targetElement}`,
      totalGuaranteedPay: `Total Guaranteed Pay at ${overallComposite.totalGuaranteedPay.targetElement}`
    };
  }

  getSJMPayElementLabels() {
    return {
      baseSalary: 'Base Salary at 50th',
      totalCash: 'Total Cash at 50th',
      totalTargetCash: 'Total Target Cash at 50th',
      totalDirect: 'Total Direct at 50th',
      totalGuaranteedPay: 'Total Guaranteed Pay at 50th',
      totalEmployeeCount: 'Total Employee Count',
      totalCompanyCount: 'Total Company Count'
    };
  }

  _hideSlideout() {
    const { hideSlideout, actions } = this.props;
    actions.resetOverallComposite();
    hideSlideout();
  }

  getPageLoadingRender() {
    return (
      <section className="year-over-year__container">
        <div className="year-over-year">
          <YearOverYearHeader closeSlideout={this._hideSlideout} />
          <div className="overall-composites__content overall-composites__is-loading">
            <InPlaceIndicator className="year-over-year__loading" />
          </div>
        </div>
      </section>
    );
  }

  render() {
    const data = this.props.overallComposite;

    const {
      selectedPayElement,
      actions,
      selectedOrgJob,
      isCompositeLoading,
      hideJobCodeKey,
      jobCodeKeyText,
      showAsPercentage,
      showOverallComposites,
      launchDarklyFeatureFlags,
      surveyJobMatches,
      isSurveyJobMatchesLoading,
      errorLoadingSurveyJobMatches,
      showSurveyJobDrilldown,
      monetaryRounding,
      percentRounding,
      selectedBenchmarkYear,
      displayIn
    } = this.props;

    const { isPdf } = this.state;
    const isPageLoading = isCompositeLoading || Object.keys(selectedOrgJob) < 1 || !data;
    const isSJMCount = selectedPayElement === 'totalEmployeeCount' || selectedPayElement === 'totalCompanyCount';
    const renderPayElement = showOverallComposites && isSJMCount ? 'baseSalary' : selectedPayElement;
    if (isPageLoading && !isPdf) {
      return this.getPageLoadingRender();
    }

    if (!data) {
      return null;
    }

    const payElementLabels = this.getPayElementLabels();
    let component;
    if (isPdf) {
      const overallCompositeChartData = this.getChartData(
        data,
        selectedPayElement,
        payElementLabels[selectedPayElement]
      );
      let surveyJobMatchesChartData = null;
      const { drilldownJob } = this.props;
      const sjmPayElementLabels = this.getSJMPayElementLabels();

      if (showSurveyJobDrilldown) {
        surveyJobMatchesChartData = this.getChartData(
          drilldownJob,
          selectedPayElement,
          payElementLabels[selectedPayElement]
        );
      }

      component = (
        <div className="year-over-year-print">
          <SlideoutBody>
            <OrgJobInfo
              selectedOrgJob={selectedOrgJob}
              hideJobCodeKey={hideJobCodeKey}
              jobCodeKeyText={jobCodeKeyText}
            />
            <OverallComposites
              chartData={overallCompositeChartData}
              selectedOrgJob={selectedOrgJob}
              selectedPayElement={renderPayElement}
              payElementLabels={payElementLabels}
              actions={actions}
              data={data}
              isPdf={isPdf}
              showAsPercentage={showAsPercentage}
              hideJobCodeKey={hideJobCodeKey}
              jobCodeKeyText={jobCodeKeyText}
              showOverallComposites={showOverallComposites}
            />
            <br />
            <div className="survey-job-matches__header">
              <br />
              <h4 className="header-xxsmall">Original &amp; Unaged Data for Survey Job Matches</h4>
            </div>
            <SurveyJobMatches
              selectedPayElement={renderPayElement}
              payElementLabels={sjmPayElementLabels}
              actions={actions}
              benchmarkYear={data.baseSalary.currentYear.label}
              surveyJobMatches={surveyJobMatches}
              loading={isSurveyJobMatchesLoading}
              monetaryRounding={monetaryRounding}
              percentRounding={percentRounding}
              errorLoadingSurveyJobMatches={errorLoadingSurveyJobMatches}
              showDrilldown={showSurveyJobDrilldown}
              drilldownJob={drilldownJob}
              showSurveyJobDrilldownAction={actions.showSurveyJobDrilldown}
              showSurveyJobMatches={actions.showSurveyJobMatches}
              chartData={surveyJobMatchesChartData}
              isPdf={isPdf}
              showAsPercentage={showAsPercentage}
              showOverallComposites={showOverallComposites}
            />
          </SlideoutBody>
        </div>
      );
    } else if (showOverallComposites) {
      const chartData = this.getChartData(data, selectedPayElement, payElementLabels[selectedPayElement]);

      component = (
        <OverallComposites
          chartData={chartData}
          selectedOrgJob={selectedOrgJob}
          selectedPayElement={renderPayElement}
          payElementLabels={payElementLabels}
          actions={actions}
          data={data}
          showAsPercentage={showAsPercentage}
          hideJobCodeKey={hideJobCodeKey}
          jobCodeKeyText={jobCodeKeyText}
          showOverallComposites={showOverallComposites}
        />
      );
    } else {
      const { drilldownJob } = this.props;
      let chartData = null;
      const sjmPayElementLabels = this.getSJMPayElementLabels();

      if (showSurveyJobDrilldown) {
        chartData = this.getChartData(drilldownJob, selectedPayElement, payElementLabels[selectedPayElement]);
      }

      component = (
        <SurveyJobMatches
          selectedPayElement={renderPayElement}
          payElementLabels={sjmPayElementLabels}
          actions={actions}
          benchmarkYear={data.baseSalary.currentYear.label}
          surveyJobMatches={surveyJobMatches}
          loading={isSurveyJobMatchesLoading}
          monetaryRounding={monetaryRounding}
          percentRounding={percentRounding}
          errorLoadingSurveyJobMatches={errorLoadingSurveyJobMatches}
          showDrilldown={showSurveyJobDrilldown}
          drilldownJob={drilldownJob}
          showSurveyJobDrilldownAction={actions.showSurveyJobDrilldown}
          showSurveyJobMatches={actions.showSurveyJobMatches}
          chartData={chartData}
          metadata={data.metadata}
          showAsPercentage={showAsPercentage}
          showOverallComposites={showOverallComposites}
        />
      );
    }

    return isPdf ? (
      <div>{component}</div>
    ) : (
      <section className="year-over-year__container">
        <div className="year-over-year">
          <YearOverYearHeader closeSlideout={this._hideSlideout} />
          <OrgJobInfo selectedOrgJob={selectedOrgJob} hideJobCodeKey={hideJobCodeKey} jobCodeKeyText={jobCodeKeyText} />
          <ViewSwitcher
            showOverallCompositesAction={actions.showOverallComposites}
            showSurveyJobMatches={actions.showSurveyJobMatches}
            showOverallComposites={showOverallComposites}
          />
          {component}
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ yearOverYear, aging }) => {
  return {
    ...yearOverYear,
    defaultBenchmarkYear: aging.benchmarkYear
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...yoyActionCreator }, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(YearOverYearContainer));
